import {
  ChartBlockChartTypeEnum,
  ChartUserOverrides,
  ChartUserOverridesChartTypeEnum,
  ChartUserOverridesDataLabelsEnum,
  ChartUserOverridesLegendPositionEnum,
  ChartUserOverridesStackingEnum,
} from "@coeff/agent-types";

import {
  axesBySide,
  axisSideOfScale,
  chartTypeHasAxes,
  chartTypeStacks,
  ResolvedAxes,
} from "./chartCapabilities";
import { formatChartNumber } from "./chartLabelEnforcement";
import type { ChartOptions, DashboardWindow } from "./dashboardHelpers";

/**
 * Manual chart adjustments from the block editor (`chart_overrides`), applied by createChart on
 * top of the config the render JS builds. Stored outside render_function_js so they survive AI
 * edits and refreshes.
 */

type AnyRecord = Record<string, any>;

// One color per value, not per dataset: pie slices, doughnut arcs, funnel stages.
export const PER_SEGMENT_CHART_TYPES: ReadonlySet<string> = new Set<ChartBlockChartTypeEnum>([
  "pie",
  "doughnut",
  "funnel",
]);

/** What the popover shows for a chart block: current values per control. */
export interface ChartEditSettings {
  effectiveType: string;
  stacking: ChartUserOverridesStackingEnum;
  legendPosition: ChartUserOverridesLegendPositionEnum;
  dataLabels: ChartUserOverridesDataLabelsEnum;
  showXAxis: boolean;
  showYAxis: boolean;
  /** Stacking is hidden below 2. */
  seriesCount: number;
  /** 2 for a dual-axis chart; percent stacking needs 1. */
  valueAxisCount: number;
  /** Whether the render JS configured `plugins.datalabels` itself. */
  hasOwnDatalabels: boolean;
}

/**
 * Overrides are only applied inside createChart, so blocks that render plain HTML/SVG never
 * read them and the popover offers AI edit only. A string sniff is deliberately coarse: it
 * runs before the chart mounts, and a false positive just shows controls that no-op.
 */
export const rendersWithCreateChart = (renderFunctionJs: string | null | undefined): boolean =>
  Boolean(renderFunctionJs?.includes("createChart("));

export const isSwitchableChartType = (type: string): type is ChartUserOverridesChartTypeEnum =>
  Object.values<string>(ChartUserOverridesChartTypeEnum).includes(type);

const isRecord = (value: unknown): value is AnyRecord =>
  typeof value === "object" && value !== null;

/**
 * Chart.js has no horizontalBar or area type (they are bar+indexAxis:'y' and line+fill), pie
 * and doughnut differ only by an explicit cutout the render JS often writes, and "matrix" is
 * its name for our heatmap. Recover the user-facing type from the config.
 */
export const getEffectiveChartType = (
  type: string,
  options: AnyRecord | undefined,
  datasets: Array<AnyRecord> | undefined
): string => {
  if (type === "matrix") {
    return "heatmap";
  }
  if (type === "bar" && options?.indexAxis === "y") {
    return "horizontalBar";
  }
  if (type === "line" && (datasets ?? []).some(ds => Boolean(ds.fill))) {
    return "area";
  }
  if (type === "pie" || type === "doughnut") {
    // Dataset-level cutout wins in Chart.js resolution.
    const cutout = (datasets ?? []).find(ds => ds.cutout !== undefined)?.cutout ?? options?.cutout;
    if (cutout !== undefined) {
      const hole = typeof cutout === "number" ? cutout : parseFloat(String(cutout));
      return hole > 0 ? "doughnut" : "pie";
    }
  }
  return type;
};

/** Which side carries the categories; undefined for segment types, which have no orientation. */
const cartesianIndexSide = (type: string): "x" | "y" | undefined => {
  if (PER_SEGMENT_CHART_TYPES.has(type)) {
    return undefined;
  }
  return type === "horizontalBar" ? "y" : "x";
};

const cartesianScaleIdsBySide = (options: AnyRecord): { x: string[]; y: string[] } => {
  const scales = options.scales;
  const x: string[] = [];
  const y: string[] = [];
  if (isRecord(scales)) {
    for (const id of Object.keys(scales)) {
      const side = axisSideOfScale(id, scales[id]);
      if (side === "x") {
        x.push(id);
      } else if (side === "y") {
        y.push(id);
      }
    }
  }
  return { x, y };
};

/**
 * Scale keys that belong to the axis's role (categories vs values), not its id, so an
 * orientation flip moves them across. Left behind, the render JS's number formatter lands on
 * the new category axis and prints 0, 1, 2 … instead of labels. `position` is id-bound and
 * dropped instead; `grid` and `stacked` stay (the stacking step rewrites `stacked` anyway).
 */
const ROLE_BOUND_SCALE_KEYS = [
  "type",
  "title",
  "ticks",
  "beginAtZero",
  "min",
  "max",
  "suggestedMin",
  "suggestedMax",
] as const;

/**
 * A side the block never configured is created at the default id, so a lone value axis's
 * formatter still moves. Caller guarantees at most one scale per side (canApplyTypeSwitch).
 */
const swapCartesianAxisRoles = (options: AnyRecord): void => {
  const { x: xIds, y: yIds } = cartesianScaleIdsBySide(options);
  const scales = options.scales;
  const xId = xIds[0];
  const yId = yIds[0];
  if (!isRecord(scales) || xIds.length > 1 || yIds.length > 1 || (!xId && !yId)) {
    return;
  }
  const x = (scales[xId ?? "x"] ||= {});
  const y = (scales[yId ?? "y"] ||= {});
  const roleBound = (scale: AnyRecord): AnyRecord =>
    Object.fromEntries(
      ROLE_BOUND_SCALE_KEYS.filter(key => scale[key] !== undefined).map(key => [key, scale[key]])
    );
  const fromX = roleBound(x);
  const fromY = roleBound(y);
  for (const key of ROLE_BOUND_SCALE_KEYS) {
    delete x[key];
    delete y[key];
  }
  Object.assign(x, fromY);
  Object.assign(y, fromX);
  delete x.position;
  delete y.position;
};

/**
 * An orientation flip on a dual-axis chart has no partner for the second value axis, so that
 * switch is refused. The popover does not offer it; this guards a persisted value.
 */
const canApplyTypeSwitch = (
  targetType: string,
  currentType: string,
  options: AnyRecord
): boolean => {
  const from = cartesianIndexSide(currentType);
  const to = cartesianIndexSide(targetType);
  if (from === undefined || to === undefined || from === to) {
    return true;
  }
  const { x, y } = cartesianScaleIdsBySide(options);
  return x.length <= 1 && y.length <= 1;
};

/** Room beside a horizontal element for an outside label: ~6 chars of 12px bold text. */
const OUTSIDE_LABEL_SIDE_GUTTER_PX = 56;
/** Top/bottom room for outside labels on segment charts. */
const OUTSIDE_LABEL_SEGMENT_PADDING_PX = 24;

/** Pie, doughnut or funnel. Returns whether it wrote layout padding. */
const switchToSegmentType = (
  chartOptions: ChartOptions,
  targetType: ChartUserOverridesChartTypeEnum
): boolean => {
  const options = chartOptions.options as AnyRecord;
  const datasets = chartOptions.data?.datasets ?? [];
  chartOptions.type = targetType;
  // Leftover cartesian scales still paint stray borders on a segment chart.
  delete options.scales;
  datasets.forEach(ds => {
    delete ds.cutout;
    delete ds.fill;
  });
  // A render-JS cutout (`type:'doughnut', cutout:'62%'`) would beat the target's default.
  if (targetType === "pie") {
    options.cutout = 0;
  } else if (targetType === "doughnut") {
    options.cutout = "50%";
  } else {
    delete options.cutout;
  }
  if (targetType !== "funnel") {
    delete options.indexAxis;
    return false;
  }
  // Side gutters keep funnel labels and slanted edges off the card edge.
  options.indexAxis = "y";
  const layout = (options.layout ||= {});
  if (layout.padding !== undefined) {
    return false;
  }
  layout.padding = { left: OUTSIDE_LABEL_SIDE_GUTTER_PX, right: OUTSIDE_LABEL_SIDE_GUTTER_PX };
  return true;
};

/** Bar, horizontal bar, line or area. */
const switchToCartesianType = (
  chartOptions: ChartOptions,
  targetType: ChartUserOverridesChartTypeEnum,
  currentType: string
): void => {
  const options = chartOptions.options as AnyRecord;
  const datasets = chartOptions.data?.datasets ?? [];
  // createChart converts a literal "horizontalBar" to bar+indexAxis:'y' right after this.
  chartOptions.type = targetType === "area" ? "line" : targetType;
  const fromSide = cartesianIndexSide(currentType);
  const toSide = cartesianIndexSide(targetType);
  if (fromSide !== undefined && toSide !== undefined && fromSide !== toSide) {
    swapCartesianAxisRoles(options);
  }
  options.indexAxis = targetType === "horizontalBar" ? "y" : "x";
  delete options.cutout;
  const fillsArea = targetType === "area";
  // A segment source colors per value (arrays); drop those so the theme recolors per series.
  const fromSegmentType = PER_SEGMENT_CHART_TYPES.has(currentType);
  datasets.forEach(ds => {
    delete ds.cutout;
    ds.fill = fillsArea;
    if (fromSegmentType && Array.isArray(ds.backgroundColor)) {
      delete ds.backgroundColor;
    }
    if (fromSegmentType && Array.isArray(ds.borderColor)) {
      delete ds.borderColor;
    }
  });
};

interface TypeSwitchResult {
  effectiveType: string;
  /** The switch wrote layout padding (funnel gutters), which the label step may replace. */
  ownsPadding: boolean;
}

const applyTypeSwitch = (
  chartOptions: ChartOptions,
  targetType: ChartUserOverrides["chart_type"]
): TypeSwitchResult => {
  const options = chartOptions.options as AnyRecord;
  const datasets = chartOptions.data?.datasets ?? [];
  const currentType = getEffectiveChartType(chartOptions.type, options, datasets);
  if (
    !targetType ||
    targetType === currentType ||
    !isSwitchableChartType(currentType) ||
    !canApplyTypeSwitch(targetType, currentType, options)
  ) {
    return { effectiveType: currentType, ownsPadding: false };
  }
  if (PER_SEGMENT_CHART_TYPES.has(targetType)) {
    return {
      effectiveType: targetType,
      ownsPadding: switchToSegmentType(chartOptions, targetType),
    };
  }
  switchToCartesianType(chartOptions, targetType, currentType);
  return { effectiveType: targetType, ownsPadding: false };
};

const isFiniteNumberArray = (values: unknown[]): values is number[] =>
  values.every(value => typeof value === "number" && Number.isFinite(value));

/**
 * "100% stacked": each value becomes its share of the per-index total. Needs 2+ datasets (one
 * series is 100% of itself everywhere) of equal-length plain numbers; anything else degrades
 * to plain stacking.
 */
const applyPercentStacking = (chartOptions: ChartOptions, valueAxisId: string): void => {
  const datasets = chartOptions.data?.datasets ?? [];
  if (datasets.length < 2) {
    return;
  }
  const length = Array.isArray(datasets[0]?.data) ? datasets[0].data.length : 0;
  if (
    length === 0 ||
    !datasets.every(ds => Array.isArray(ds.data) && ds.data.length === length) ||
    !datasets.every(ds => isFiniteNumberArray(ds.data))
  ) {
    return;
  }

  const totals = Array.from({ length }, (_, index) =>
    datasets.reduce((sum, ds) => sum + Math.abs((ds.data as number[])[index]), 0)
  );
  datasets.forEach(ds => {
    ds.data = (ds.data as number[]).map((value, index) =>
      totals[index] === 0 ? 0 : Math.round((value / totals[index]) * 1000) / 10
    );
  });

  const options = (chartOptions.options ||= {}) as AnyRecord;
  const scales = (options.scales ||= {});
  const axis = (scales[valueAxisId] ||= {});
  axis.max = 100;
  const ticks = (axis.ticks ||= {});
  if (typeof ticks.callback !== "function") {
    ticks.callback = (value: unknown) => `${value}%`;
  }
};

const applyStacking = (
  chartOptions: ChartOptions,
  stacking: ChartUserOverrides["stacking"],
  effectiveType: string,
  axes: ResolvedAxes
): void => {
  if (!stacking || !chartTypeStacks(effectiveType)) {
    return;
  }
  const options = chartOptions.options as AnyRecord;
  const scales = (options.scales ||= {});
  for (const id of [...axes.x, ...axes.y]) {
    (scales[id] ||= {}).stacked = stacking !== "none";
  }
  // Percent stacking across two value axes would mix units, so it degrades to plain there.
  const valueAxisIds = axes[axes.valueSide];
  if (stacking === "percent" && valueAxisIds.length === 1) {
    applyPercentStacking(chartOptions, valueAxisIds[0]);
  }
};

const applyLegend = (options: AnyRecord, position: ChartUserOverrides["legend_position"]): void => {
  if (!position) {
    return;
  }
  const plugins = (options.plugins ||= {});
  const legend = (plugins.legend ||= {});
  legend.display = position !== "none";
  if (position !== "none") {
    legend.position = position;
  }
};

const outsideLabelPadding = (effectiveType: string): AnyRecord | number | undefined => {
  if (effectiveType === "funnel") {
    // Labels sit beside the widest stage, so both sides need a gutter.
    return {
      top: OUTSIDE_LABEL_SEGMENT_PADDING_PX,
      bottom: OUTSIDE_LABEL_SEGMENT_PADDING_PX,
      left: OUTSIDE_LABEL_SIDE_GUTTER_PX,
      right: OUTSIDE_LABEL_SIDE_GUTTER_PX,
    };
  }
  if (PER_SEGMENT_CHART_TYPES.has(effectiveType)) {
    return OUTSIDE_LABEL_SEGMENT_PADDING_PX;
  }
  if (effectiveType === "horizontalBar") {
    return { right: OUTSIDE_LABEL_SIDE_GUTTER_PX };
  }
  return undefined;
};

/**
 * Segment and horizontal-bar charts fill the chart area, so an outside label clips at the
 * canvas unless layout padding makes room (`clip: false` only allows drawing outside the chart
 * area). A block's own padding is kept; padding the type switch wrote is ours to replace.
 */
const reserveOutsideLabelRoom = (
  options: AnyRecord,
  effectiveType: string,
  ownsPadding: boolean
): void => {
  const padding = outsideLabelPadding(effectiveType);
  if (padding === undefined) {
    return;
  }
  const layout = (options.layout ||= {});
  if (layout.padding === undefined || ownsPadding) {
    layout.padding = padding;
  }
};

const DATALABEL_PLACEMENT = {
  inside: { anchor: "center", align: "center" },
  outside: { anchor: "end", align: "end" },
} as const;

const applyDataLabels = (
  chartOptions: ChartOptions,
  mode: ChartUserOverrides["data_labels"],
  effectiveType: string,
  ownsPadding: boolean,
  chartDataLabelsPlugin?: unknown
): void => {
  if (!mode) {
    return;
  }
  const options = chartOptions.options as AnyRecord;
  const datasets = chartOptions.data?.datasets ?? [];
  const plugins = (options.plugins ||= {});
  const datalabels = (plugins.datalabels ||= {});
  // Dataset-level datalabels beat chart-level ones in Chart.js; block formatting stays.
  datasets.forEach(ds => {
    if (isRecord(ds.datalabels)) {
      delete ds.datalabels.display;
      delete ds.datalabels.anchor;
      delete ds.datalabels.align;
    }
  });
  if (mode === "none") {
    datalabels.display = false;
    return;
  }
  datalabels.display = true;
  Object.assign(datalabels, DATALABEL_PLACEMENT[mode]);
  datalabels.clip = false;
  if (typeof datalabels.formatter !== "function") {
    datalabels.formatter = (value: unknown) =>
      typeof value === "number" ? formatChartNumber(value) : value;
  }
  if (mode === "outside") {
    reserveOutsideLabelRoom(options, effectiveType, ownsPadding);
  }
  // The plugin is CDN-loaded but never registered globally.
  if (chartDataLabelsPlugin) {
    const list = (chartOptions.plugins ||= []);
    if (!list.includes(chartDataLabelsPlugin)) {
      list.push(chartDataLabelsPlugin);
    }
  }
};

// "Y-axis" means every scale on the vertical side, so a dual-axis chart toggles both together.
const applyAxisVisibility = (
  options: AnyRecord,
  overrides: ChartUserOverrides,
  effectiveType: string,
  axes: ResolvedAxes
): void => {
  if (!chartTypeHasAxes(effectiveType)) {
    return;
  }
  const sides: Array<[unknown, string[]]> = [
    [overrides.show_x_axis, axes.x],
    [overrides.show_y_axis, axes.y],
  ];
  for (const [shown, ids] of sides) {
    if (typeof shown !== "boolean") {
      continue;
    }
    const scales = (options.scales ||= {});
    for (const id of ids) {
      (scales[id] ||= {}).display = shown;
    }
  }
};

/**
 * Runs in createChart before the horizontalBar conversion and theming, so a switched type
 * flows through both. `chartDataLabelsPlugin` is the iframe's ChartDataLabels global.
 */
export const applyChartUserOverrides = (
  chartOptions: ChartOptions,
  overrides: ChartUserOverrides,
  chartDataLabelsPlugin?: unknown
): void => {
  const options = (chartOptions.options ||= {}) as AnyRecord;
  const { effectiveType, ownsPadding } = applyTypeSwitch(chartOptions, overrides.chart_type);
  // Writing a literal `scales.y` on a yLeft/yRight chart would add a third axis.
  const axes = axesBySide(chartOptions);

  applyStacking(chartOptions, overrides.stacking, effectiveType, axes);
  applyLegend(options, overrides.legend_position);
  applyDataLabels(
    chartOptions,
    overrides.data_labels,
    effectiveType,
    ownsPadding,
    chartDataLabelsPlugin
  );
  applyAxisVisibility(options, overrides, effectiveType, axes);
};

/**
 * Current settings off the live Chart.js instance, so the popover starts from what is rendered.
 * Null when the chart isn't mounted; callers fall back to spec values.
 */
export const readLiveChartSettings = (
  targetWindow: DashboardWindow,
  fullBlockId: string
): ChartEditSettings | null => {
  try {
    const win = targetWindow as unknown as AnyRecord;
    const card = targetWindow.document.getElementById(fullBlockId);
    const canvas = card?.querySelector(".chart-canvas");
    const chart = canvas ? win.Chart?.getChart?.(canvas) : null;
    if (!chart) {
      return null;
    }

    // The raw config, not `chart.options`: that is the resolver proxy, and reading some keys
    // through it throws "Recursion detected".
    const options: AnyRecord = chart.config?.options ?? {};
    const datasets: Array<AnyRecord> = chart.config?.data?.datasets ?? [];
    const effectiveType = getEffectiveChartType(chart.config?.type ?? "", options, datasets);

    const legend = options.plugins?.legend;
    const legendPosition: ChartUserOverridesLegendPositionEnum =
      legend?.display === false
        ? "none"
        : (legend?.position as ChartUserOverridesLegendPositionEnum) ?? "top";

    const datalabels = options.plugins?.datalabels;
    let dataLabels: ChartUserOverridesDataLabelsEnum = "none";
    if (datalabels && datalabels.display !== false) {
      dataLabels = datalabels.anchor === "end" ? "outside" : "inside";
    }

    const scales: AnyRecord = options.scales ?? {};
    const axes = axesBySide({ options });
    const allStacked = (ids: string[]) => ids.every(id => scales[id]?.stacked === true);
    const stacking: ChartUserOverridesStackingEnum =
      allStacked(axes.x) && allStacked(axes.y) ? "stacked" : "none";
    const sideShown = (ids: string[]) => ids.some(id => scales[id]?.display !== false);

    return {
      effectiveType,
      stacking,
      legendPosition,
      dataLabels,
      showXAxis: sideShown(axes.x),
      showYAxis: sideShown(axes.y),
      seriesCount: datasets.length,
      valueAxisCount: axes[axes.valueSide].length,
      hasOwnDatalabels: Boolean(datalabels && typeof datalabels === "object"),
    };
  } catch {
    // A block's exotic config must never take down the editor.
    console.warn(`Failed to read live chart settings for block ${fullBlockId}`);
    return null;
  }
};

/**
 * Every override reaches the chart by re-running the render JS, so createChart rebuilds from
 * scratch. A cheaper live path (chart.update with only the keys present) was removed: it could
 * add an override but never remove one, so an undo left the stale value in the config until an
 * unrelated rebuild. One SQL re-run per control click is the accepted cost.
 */
const CHART_OVERRIDE_KEYS = [
  "chart_type",
  "stacking",
  "legend_position",
  "data_labels",
  "show_x_axis",
  "show_y_axis",
] as const satisfies readonly (keyof ChartUserOverrides)[];

// Fixed key order and no nulls, so two spellings of the same override set share a signature.
const pickOverrides = (overrides: ChartUserOverrides | null | undefined): ChartUserOverrides => {
  const picked: ChartUserOverrides = {};
  if (!overrides) {
    return picked;
  }
  for (const key of CHART_OVERRIDE_KEYS) {
    const value = overrides[key];
    if (value !== undefined && value !== null) {
      (picked as AnyRecord)[key] = value;
    }
  }
  return picked;
};

/** Feeds useBlockRender's deps; any change, including a removed key, re-runs the render. */
export const getChartRebuildSignature = (
  overrides: ChartUserOverrides | null | undefined
): string => JSON.stringify(pickOverrides(overrides));
